import React, { useState, useEffect } from 'react';
import ApiClient from '../../../api/apiClient';
import { useAuth } from '../../../context/AuthContext';
import { useData } from '../../../context/DataContext';
import './EditModal.css';

const URL_REGEX = /^https?:\/\/[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}(:[0-9]{1,5})?(\/.*)?$/;

const EditModal = ({ show, onClose, server, onUpdateSuccess }) => {
  const [name, setName] = useState(server?.name || '');
  const [link, setLink] = useState(server?.link || '');
  const [status, setStatus] = useState(server?.status || '');
  const [env, setEnv] = useState(server?.env?._id || '');
  const [phases, setPhases] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [updateResult, setUpdateResult] = useState('');
  const { user } = useAuth();
  const { environments, refreshEnvironments } = useData();
  const organization = user?.organization || 'default';

  useEffect(() => {
    if (!show) return;

    setError('');
    setUpdateResult('');
    setPhases(environments || []);

    const fetchPhases = async () => {
      try {
        const fresh = await refreshEnvironments();
        setPhases(fresh);
      } catch (err) {
        console.error('Error fetching phases:', err);
        setError('Error fetching phases');
      }
    };

    if (!environments?.length) {
      fetchPhases();
    }
  }, [environments, refreshEnvironments, show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!URL_REGEX.test(link)) {
      setError('Invalid URL format.');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await ApiClient.put(
        `/api/servers/${server._id}`,
        { name, link, status, env },
        { params: { organization } }
      );

      if (response.status === 200) {
        if (typeof onUpdateSuccess === 'function') {
          onUpdateSuccess(); // Call onUpdateSuccess instead of fetchServers
        } else {
          console.error('onUpdateSuccess is not a function');
        }
        setUpdateResult('Server updated successfully');
        setTimeout(() => setUpdateResult(''), 3000); // Clear message after 3 seconds
        // Removed onClose() here to keep the modal open
      } else {
        setError(`Failed to update server. Status code: ${response.status}`);
      }
    } catch (err) {
      console.error('Error updating server:', err.response || err);
      setError(`Error updating server: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePhaseChange = async (newEnvId) => {
    try {
      const response = await ApiClient.put(
        `/api/servers/dev/${server._id}/phase`,
        { envId: newEnvId },
        { params: { organization } }
      );
      if (response.status === 200) {
        if (typeof onUpdateSuccess === 'function') {
          onUpdateSuccess(); // Refresh the servers data after phase update
        } else {
          console.error('onUpdateSuccess is not a function');
        }
      } else {
        setError('Failed to update phase.');
      }
    } catch (err) {
      console.error('Error updating server phase:', err);
      setError('Error updating server phase');
    }
  };

  useEffect(() => {
    if (server) {
      setName(server.name || '');
      setLink(server.link || '');
      setStatus(server.status || '');
      setEnv(server.env?._id || '');
    }
  }, [server]);

  if (!show) return null;

  return (
    <div className="modal-overlay edit-modal">
      <div className="modal-content edit-modal-card">
        <div className="modal-header">
          <div>
            <p className="eyebrow">Edit server</p>
            <h2>Edit Server</h2>
          </div>
          <button type="button" className="ghost-button" onClick={onClose} disabled={isSubmitting}>
            Close
          </button>
        </div>
        {error && <div className="notification error">{error}</div>}
        {updateResult && <div className="notification success">{updateResult}</div>}
        <form onSubmit={handleSubmit}>
          <label>
            Name
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Server name"
            />
          </label>
          <label>
            Link
            <input
              type="text"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="https://example.com"
            />
          </label>
          <label>
            Phase
            <select
              value={env}
              onChange={(e) => {
                setEnv(e.target.value);
                handlePhaseChange(e.target.value); // Call handlePhaseChange
              }}
            >
              <option value="">Select Phase</option>
              {phases.map(phase => (
                <option key={phase._id} value={phase._id}>
                  {phase.name}
                </option>
              ))}
            </select>
          </label>
          <div className="modal-actions">
            <button className="ghost-button" type="button" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button className="primary-button" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditModal;
